import React, { useState, useMemo, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { StarRating } from '../components/StarRating';
import { TagsList } from '../components/TagsList';
import { TAGS_BY_RATING_SUBSCRIPTION, Tag } from './constants';
import {
  useDomRef,
  Container_v2 as Container,
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Typography,
  TypographyVariantsEnum,
  IconReviewSessionSubscription,
} from '@mindly/ui-components';

type SessionReviewProps = {
  specialistName: string;
  onSubmit: (data: { rate: number; reasons: string[]; comment?: string }) => Promise<void>;
  specialistReviewNeeded?: boolean;
  onModalAppeared?: () => void;
  onStarsClick?: (rate: number) => void;
  onReasonClick?: (rate: number, reasons: string[]) => void;
  onCommentBlur?: (rate: number, reasons: string[], comment: string) => void;
  onContinueClick?: (rate: number, reasons: string[], comment: string) => void;
};

export const SessionReview: React.FC<SessionReviewProps> = ({
  specialistName,
  specialistReviewNeeded,
  onSubmit,
  onModalAppeared,
  onStarsClick,
  onReasonClick,
  onCommentBlur,
  onContinueClick,
}) => {
  const containerRef = useDomRef<HTMLIonContentElement>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<number>(1);
  const { t } = useTranslation('reviews');

  const currentTags = useMemo(() => (rate ? TAGS_BY_RATING_SUBSCRIPTION(t)[rate] : []), [rate]);

  useEffect(() => {
    onModalAppeared?.();
  }, []);

  const handleStarSelect = (value: number) => {
    setRate(value);
    setSelectedTags([]);
    setComment('');
    onStarsClick?.(value);
    setStep(2);
  };

  const handleTagClick = (tag: Tag) => {
    flushSync(() => {
      let newTags = [...selectedTags];

      if (newTags.some((_) => _.id === tag.id)) {
        newTags = newTags.filter((_) => _.id !== tag.id);
      } else {
        newTags.push(tag);
      }
      setSelectedTags(newTags);
      if (newTags.some((_) => _.id !== 'other')) {
        setComment('');
      }
      rate &&
        onReasonClick?.(
          rate,
          newTags.map((_) => _.name)
        );
    });
  };

  const handleCommentBlur = () => {
    if (rate && selectedTags?.length) {
      onCommentBlur?.(
        rate,
        selectedTags.map((_) => _.name),
        comment
      );
    }
  };

  const handleContinue = async () => {
    if (rate && selectedTags?.length) {
      onContinueClick?.(
        rate,
        selectedTags.map((tag) => tag.name),
        comment
      );
      setLoading(true);
      try {
        await onSubmit({
          rate,
          reasons: selectedTags.map((tag) => tag.name),
          comment: selectedTags.some((tag) => tag.id === 'other') ? comment : undefined,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const isButtonDisabled =
    !rate ||
    !selectedTags?.length ||
    (selectedTags.some((tag) => tag.id === 'other') && (!comment.trim() || comment.trim().length < 2));

  return (
    <>
      <Container ref={containerRef} className="flex flex-col [&::part(scroll)]:flex [&::part(scroll)]:flex-col">
        {specialistReviewNeeded && (
          <div className="flex w-[70%] h-1 justify-around rounded-full overflow-hidden mx-auto">
            <div className={`transition-all h-full bg-color-background-gray-black w-[45%]`} />
            <div className={`transition-all h-full bg-color-background-gray-black/6 w-[45%]`} />
          </div>
        )}

        <div className="flex justify-center items-center mx-auto mb-5 mt-20">
          <IconReviewSessionSubscription />
        </div>

        <Typography variant={TypographyVariantsEnum.Text24Semi} className="text-center mb-3">
          {t?.('rate_session_title', {
            specialistFullName: specialistName,
          })}
        </Typography>
        <Typography
          variant={TypographyVariantsEnum.Text14Regular}
          className="text-typography-typography-secondary text-center mb-5 opacity-50"
        >
          {t?.('rate_session_description')}
        </Typography>

        {step === 1 && <StarRating onSelect={handleStarSelect} selected={rate} />}

        {step === 2 && (
          <TagsList
            tags={currentTags}
            onClick={handleTagClick}
            selected={selectedTags}
            isTrialSession={false}
            comment={comment}
            onCommentChange={setComment}
            onCommentBlur={handleCommentBlur}
          />
        )}
      </Container>
      <AppFooter className="pb-6">
        <Button size="large" fullWidth onClick={handleContinue} disabled={isButtonDisabled} isLoading={loading}>
          {t?.('button_label')}
        </Button>
      </AppFooter>
    </>
  );
};
